/**
 * One client's automated calls: the switch, and its latest calls.
 *
 * Off by default, and off means the client has no trace of calls. It can be
 * switched on only while calling is on for Connect (Automated calls); switching
 * it off cancels whatever was waiting to be dialled for the client.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Alert,
  ApiError,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  QueryPending,
  ReadOnly,
  StatusChip,
  Switch,
  api,
  formatDateTime,
  toast,
  useCan,
} from '@connect/web-shared';

import { CALL_STATUS, callingKeys, type CallingClient } from './calling';

const TONES = Object.fromEntries(Object.entries(CALL_STATUS).map(([k, v]) => [k, v.tone]));
const LABELS = Object.fromEntries(Object.entries(CALL_STATUS).map(([k, v]) => [k, v.label]));

export function CallingClientCard({ clientPublicId, clientName }: { clientPublicId: string; clientName: string }) {
  const queryClient = useQueryClient();
  const canEdit = useCan('messaging.calling.edit');
  const url = `/calling/admin/clients/${clientPublicId}`;
  const key = callingKeys.client(clientPublicId);
  const found = useQuery({ queryKey: key, queryFn: () => api.get<CallingClient>(url) });

  const toggle = useMutation({
    mutationFn: (on: boolean) => api.put<CallingClient>(url, { calling_enabled: on }),
    onSuccess: (data) => {
      queryClient.setQueryData(key, data);
      toast.success(
        data.calling_enabled ? `Calls on for ${clientName}` : `Calls off for ${clientName}`,
        data.calling_enabled ? undefined : 'Nothing waiting for it will be dialled.',
      );
    },
    onError: (e) => toast.error('Not changed', e instanceof ApiError ? e.message : undefined),
  });

  if (found.data === undefined) return <QueryPending query={found} label="Loading calls" />;
  const data = found.data;

  return (
    <ReadOnly when={!canEdit}>
      <Card>
        <CardHeader
          title="Automated calls"
          description="Calls that ring this client's customers back when a callback is missed, with a menu they answer by pressing a key."
        />
        <CardBody className="space-y-4">
          {!data.platform_ready && !data.calling_enabled ? (
            <Alert tone="info" title="Calling is off for Connect">
              Switch it on under <Link className="font-medium text-accent hover:underline" to="/messaging/calling">Automated calls</Link> first.
            </Alert>
          ) : null}
          <Switch
            label="Call this client's customers"
            hint="The client writes what the calls say, in its own app, under Voice calls."
            checked={data.calling_enabled} disabled={toggle.isPending || (!data.platform_ready && !data.calling_enabled)}
            onChange={(e) => toggle.mutate(e.target.checked)}
          />
          {data.calls.items.length === 0 ? (
            <EmptyState title="No calls yet" description="Calls appear here as they are made." />
          ) : (
            <div className="overflow-hidden rounded-control border border-line">
              <table className="w-full text-sm">
                <thead className="bg-surface-2 text-left text-xs text-muted">
                  <tr>
                    <th className="px-3 py-2 font-medium">When</th>
                    <th className="px-3 py-2 font-medium">Number</th>
                    <th className="px-3 py-2 font-medium">Why</th>
                    <th className="px-3 py-2 font-medium">State</th>
                    <th className="px-3 py-2 font-medium">Pressed</th>
                  </tr>
                </thead>
                <tbody>
                  {data.calls.items.map((call) => (
                    <tr key={call.public_id} className="border-t border-line">
                      <td className="px-3 py-2 text-muted">{formatDateTime(call.created_at)}</td>
                      <td className="px-3 py-2 font-mono text-xs text-body">{call.to_number}</td>
                      <td className="px-3 py-2 text-body">{call.purpose_label}</td>
                      <td className="px-3 py-2">
                        <StatusChip value={call.status} tones={TONES} labels={LABELS} />
                      </td>
                      <td className="px-3 py-2 text-muted">{call.pressed_label ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </ReadOnly>
  );
}
